import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Link from '@zenbusiness/zui/Link';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

export const CARD_MAX_WIDTH = 564;

const CardContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isRecommended'
})(({ theme, isRecommended }) => ({
  maxWidth: CARD_MAX_WIDTH,
  height: '100%',
  overflow: 'hidden',
  paddingInline: theme.spacing(2),
  paddingBlock: theme.spacing(2),
  borderRadius: 12,
  border: `1px solid ${isRecommended ? theme.palette.info.light : theme.palette.border.main}`,
  backgroundColor: theme.palette.background.grey.light,
  color: theme.palette.text.primary,
  justifyContent: 'space-between',
  [theme.breakpoints.up('md')]: {
    paddingBlock: theme.spacing(4.5)
  }
}));

const RecommendedBanner = styled(Box)(({ theme }) => ({
  // Bleeds to the card edges, so it has to undo CardContainer's padding.
  marginInline: theme.spacing(-2),
  marginTop: theme.spacing(-2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0.5),
  backgroundColor: theme.palette.info.main,
  [theme.breakpoints.up('md')]: {
    marginTop: theme.spacing(-4.5)
  }
}));

const Divider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: 1,
  backgroundColor: theme.palette.border.main,
  marginBlock: theme.spacing(2)
}));

const CardContent = styled(Stack)(({ theme }) => ({
  paddingInline: theme.spacing(1),
  justifyContent: 'flex-start',
  gap: theme.spacing(3)
}));

const CardAction = styled(Box)(({ theme }) => ({
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(2.5)
  }
}));

const CardLogo = ({ logo }) => (
  <Box
    alignItems="center"
    display="flex"
    height={48}
    justifyContent="center"
  >
    <SharedAsset
      alt={logo.alt}
      src={logo.src}
      width={logo.width}
    />
  </Box>
);
CardLogo.propTypes = {
  logo: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired
  }).isRequired
};

const CardTitle = ({ title, description }) => (
  <Stack
    direction="column"
    spacing={1}
  >
    <Text
      size="md"
      weight="medium"
    >
      {title}
    </Text>
    <Text size="sm">{description}</Text>
  </Stack>
);
CardTitle.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

const CardFeatures = ({ features }) => (
  <Stack spacing={1.5}>
    <Text
      size="sm"
      weight="medium"
    >
      What you get:
    </Text>
    <Stack spacing={1}>
      {features.map((feature) => (
        <Stack
          alignItems="flex-start"
          direction="row"
          key={feature}
          spacing={1}
        >
          <SharedAsset
            alt="checkrounded icon"
            color="success.main"
            flex="none"
            src="/s2/material-icons/checkrounded.svg"
            width={16}
          />
          <Text size="sm">{feature}</Text>
        </Stack>
      ))}
    </Stack>
  </Stack>
);
CardFeatures.propTypes = {
  features: PropTypes.arrayOf(PropTypes.string).isRequired
};

const CardDisclaimer = ({ disclaimer }) => (
  <Link
    href={disclaimer.hyperlink}
    onClick={disclaimer.onClick}
    rel="noreferrer"
    size="xs"
    target="_blank"
  >
    {disclaimer.anchorText}
  </Link>
);
CardDisclaimer.propTypes = {
  disclaimer: PropTypes.shape({
    hyperlink: PropTypes.string.isRequired,
    anchorText: PropTypes.string.isRequired,
    onClick: PropTypes.func
  }).isRequired
};

const CardDisclosure = ({ disclosure }) => (
  <Text
    color="tertiary"
    size="xs"
    sx={{
      flex: 1,
      display: 'flex',
      alignItems: 'flex-end'
    }}
  >
    {disclosure}
  </Text>
);
CardDisclosure.propTypes = {
  disclosure: PropTypes.string.isRequired
};

const CardCTAButton = ({ ctaButton }) => (
  <Button
    color="primary"
    fullWidth
    onClick={ctaButton.onClick}
    variant="contained"
  >
    {ctaButton.text}
  </Button>
);
CardCTAButton.propTypes = {
  ctaButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }).isRequired
};

const OfferCard = ({
  logo,
  title,
  description,
  features,
  disclaimer,
  disclosure,
  ctaButton,
  ctaSlot,
  isRecommended = false
}) => (
  <CardContainer isRecommended={isRecommended}>
    {isRecommended && (
      <RecommendedBanner>
        <Text
          align="center"
          color="white"
          size="xs"
          sx={{ textTransform: 'uppercase' }}
          weight="medium"
        >
          Recommended
        </Text>
      </RecommendedBanner>
    )}
    <CardLogo logo={logo} />
    <Divider />
    <CardContent flex={1}>
      <CardTitle
        description={description}
        title={title}
      />
      <CardFeatures features={features} />
      {disclaimer && <CardDisclaimer disclaimer={disclaimer} />}
      {disclosure && <CardDisclosure disclosure={disclosure} />}
    </CardContent>
    <CardAction>{ctaSlot || (ctaButton && <CardCTAButton ctaButton={ctaButton} />)}</CardAction>
  </CardContainer>
);
OfferCard.propTypes = {
  logo: PropTypes.shape({
    alt: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired
  }).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  disclaimer: PropTypes.shape({
    hyperlink: PropTypes.string.isRequired,
    anchorText: PropTypes.string.isRequired,
    onClick: PropTypes.func
  }),
  disclosure: PropTypes.string,
  /** Default action. Ignored when `ctaSlot` is supplied. */
  ctaButton: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired
  }),
  /** Replaces the default button, for actions that are not a plain button. */
  ctaSlot: PropTypes.node,
  isRecommended: PropTypes.bool
};

export default OfferCard;
